const CHUNK_SIZE_BYTES = 512 * 1024;
const MAX_CHUNK_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = 1000;

export interface UploadedFileRef {
  id: number;
  url: string;
  mime: string;
  name: string;
}

const parseErrorMessage = async (response: Response, fallback: string) => {
  const body = await response.json().catch(() => null);
  return body?.message ?? fallback;
};

const initUpload = async (file: File, totalChunks: number): Promise<string> => {
  const response = await fetch("/api/uploads/init", {
    method: "POST",
    credentials: "same-origin",
    mode: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fileName: file.name,
      mime: file.type || "application/octet-stream",
      totalSize: file.size,
      totalChunks
    })
  });

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, "Não foi possível iniciar o envio do arquivo.")
    );
  }

  const { uploadId } = await response.json();
  return uploadId;
};

const uploadChunk = async (uploadId: string, index: number, chunk: Blob) => {
  const response = await fetch(`/api/uploads/${uploadId}/chunks/${index}`, {
    method: "POST",
    credentials: "same-origin",
    mode: "same-origin",
    headers: { "Content-Type": "application/octet-stream" },
    body: chunk
  });

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, `Falha ao enviar parte ${index + 1} do arquivo.`)
    );
  }
};

const uploadChunkWithRetry = async (uploadId: string, index: number, chunk: Blob) => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_CHUNK_ATTEMPTS; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await uploadChunk(uploadId, index, chunk);
      return;
    } catch (error) {
      lastError = error;
      if (attempt < MAX_CHUNK_ATTEMPTS) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, RETRY_BACKOFF_MS * attempt);
        });
      }
    }
  }

  throw lastError;
};

const completeUpload = async (uploadId: string): Promise<UploadedFileRef> => {
  const response = await fetch(`/api/uploads/${uploadId}/complete`, {
    method: "POST",
    credentials: "same-origin",
    mode: "same-origin"
  });

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, "Não foi possível concluir o envio do arquivo.")
    );
  }

  return response.json();
};

export const createThrottledProgressReporter = (
  onUpdate: (fraction: number) => void,
  intervalMs = 700
) => {
  let lastReportedAt = 0;

  return (fraction: number) => {
    const now = Date.now();
    if (fraction < 1 && now - lastReportedAt < intervalMs) return;
    lastReportedAt = now;
    onUpdate(fraction);
  };
};

// Uploading all chunks only accounts for the client -> backend transfer.
// The backend still has to assemble the chunks and push the file to object
// storage before the upload is truly done, so chunk progress is capped below
// 100% until that finalize step actually completes - otherwise the UI shows
// 100% while the file is still being processed server-side.
const CHUNKS_PROGRESS_CAP = 0.95;

export const uploadFileChunked = async (
  file: File,
  onProgress?: (fraction: number) => void
): Promise<UploadedFileRef> => {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE_BYTES));
  const uploadId = await initUpload(file, totalChunks);

  onProgress?.(0);

  for (let index = 0; index < totalChunks; index += 1) {
    const start = index * CHUNK_SIZE_BYTES;
    const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);

    // eslint-disable-next-line no-await-in-loop
    await uploadChunkWithRetry(uploadId, index, file.slice(start, end));
    onProgress?.(((index + 1) / totalChunks) * CHUNKS_PROGRESS_CAP);
  }

  const result = await completeUpload(uploadId);
  onProgress?.(1);
  return result;
};
